/**
 * Vue de l'outbox et rejeu manuel (docs/15 §9).
 *
 * C'est le seul écran de l'application qu'on ouvre **pendant** un incident, et
 * il est écrit pour cela : le nombre de dead letters est affiché quelle que soit
 * la vue — l'opérateur ne doit pas avoir à changer de filtre pour découvrir qu'il
 * y a un problème —, la liste va du plus ancien au plus récent parce que ce qui
 * bloque est en tête de file, et l'erreur est montrée telle quelle plutôt que
 * traduite : c'est une trace technique, destinée à qui saura la lire.
 *
 * **Le motif est obligatoire avant de rejouer.** L'API l'exige déjà (422 sans
 * lui) ; le demander ici évite un aller-retour, mais surtout : un rejeu
 * redéclenche des effets de bord, et écrire pourquoi on le fait est le moment où
 * l'on vérifie qu'on a une raison.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useOutbox, useReplayEvent, type OutboxView } from "@/features/admin/api/useAdmin";

import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

const VIEWS: readonly OutboxView[] = ["unpublished", "pending", "failed"] as const;

export function AdminOutboxPage() {
  const { t, i18n } = useTranslation("admin");
  const [view, setView] = useState<OutboxView>("unpublished");
  const [replaying, setReplaying] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const page = useOutbox(view);
  const replay = useReplayEvent();

  const formatDateTime = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(value),
    );

  const submitReplay = (eventId: string) => {
    replay.mutate(
      { eventId, reason },
      {
        onSuccess: () => {
          setReplaying(null);
          setReason("");
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("outbox.heading")}</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("outbox.description")}</p>
      </div>

      {/* Affiché quelle que soit la vue : c'est le chiffre qui dit s'il y a un
          incident, et il ne doit pas se cacher derrière un filtre. */}
      {page.data !== undefined && page.data.dead_letter_count > 0 ? (
        <Alert tone="error">
          {t("outbox.deadLetters", { deadValue: page.data.dead_letter_count })}
        </Alert>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {VIEWS.map((candidate) => (
          <Button
            key={candidate}
            variant={candidate === view ? "primary" : "secondary"}
            onClick={() => {
              setView(candidate);
            }}
          >
            {t(`outbox.views.${candidate ?? "unpublished"}`)}
          </Button>
        ))}
      </div>

      {page.isPending ? <Skeleton className="h-64 w-full" label={t("outbox.heading")} /> : null}
      {page.isError ? <ErrorState error={page.error} /> : null}

      {page.data !== undefined ? (
        <Card className="overflow-hidden p-0">
          {page.data.items.length === 0 ? (
            <p className="p-6 text-sm text-slate-500 dark:text-slate-400">{t("outbox.empty")}</p>
          ) : (
            <ul className="divide-y divide-slate-200 dark:divide-slate-700">
              {page.data.items.map((entry) => (
                <li key={entry.event_id} className="p-4" data-testid="outbox-entry">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100">
                        {entry.event_type}
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {formatDateTime(entry.occurred_at)} ·{" "}
                        {t("outbox.attempts", { attemptsValue: entry.attempts })}
                      </p>
                    </div>
                    {entry.is_dead_letter ? (
                      <span className="rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-800 dark:bg-red-950 dark:text-red-200">
                        {t("outbox.deadLetter")}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {t("outbox.nextAttempt", { when: formatDateTime(entry.next_attempt_at) })}
                      </span>
                    )}
                  </div>

                  {/* Brute et non traduite : c'est une trace technique, et la
                      reformuler priverait de l'information celui qui la cherche. */}
                  {entry.last_error !== null ? (
                    <pre className="mt-2 overflow-x-auto rounded bg-slate-100 p-2 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      {entry.last_error}
                    </pre>
                  ) : null}

                  {replaying === entry.event_id ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <input
                        className="min-w-64 flex-1 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
                        placeholder={t("outbox.reasonPlaceholder")}
                        value={reason}
                        onChange={(event) => {
                          setReason(event.target.value);
                        }}
                        aria-label={t("outbox.reasonLabel")}
                      />
                      <Button
                        onClick={() => {
                          submitReplay(entry.event_id);
                        }}
                        disabled={replay.isPending}
                      >
                        {t("outbox.confirmReplay")}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setReplaying(null);
                        }}
                      >
                        {t("outbox.cancel")}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      className="mt-2"
                      onClick={() => {
                        setReplaying(entry.event_id);
                        setReason("");
                      }}
                    >
                      {t("outbox.replay")}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      {replay.isError ? <ErrorState error={replay.error} /> : null}
    </div>
  );
}
